import { Fragment, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import BackupIcon from '@mui/icons-material/Backup'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove'
import SyncIcon from '@mui/icons-material/Sync'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import LightModeIcon from '@mui/icons-material/LightMode'
import KeyIcon from '@mui/icons-material/Key'
import { useCurrentUser } from '../../shared/hooks/useCurrentUser'
import {
  useCustomerSearchMutation,
  useBounceCheckMutation,
  useComplaintCheckMutation,
  useUnblockEmailMutation,
  useBackupMutation,
  useBackupHistoryMutation,
  useForceFullBackupMutation,
  useUpdatePendingBankSlipsMutation,
  useSyncOroboroMutation,
  useRemoveSearchResultsWithErrorsMutation,
  useResetElasticIndexMutation,
  usePendingContractsMutation,
  useReturnToTrialMutation,
  useReturnToExpiredMutation,
  useReturnToLightMutation,
  useFirstLoginTokenMutation,
  useResetTwoFactorMutation,
} from './hooks/useCustomerQuery'

/**
 * Port of `app/customer/customers.html` + `app/customer/
 * customersController.js` (AngularJS `customersController`) — the
 * "Clientes" listing/search screen, the largest and highest-risk module in
 * the legacy app (legacy state `workspace.customers`, `/workspace/customers`
 * — see `AppRoutes.jsx`). Task 025 scope only: the main listing/search table
 * and its own row actions (backup, bank slips, Oroboro sync, Elasticsearch
 * reset/cleanup, bounce/complaint/unblock, return-to-trial/light, first
 * login token). Sub-screens reached from this table's "Habilitar limpeza de
 * conta" action (`workspace.customerProfile`) and any customer-edit flow
 * are explicitly OUT of scope here — tasks 026-029 own them (see this
 * task's spec, `specs/react-migration/025-module-customer-list.md`).
 *
 * `customersController.js` and `backupService.js` are flagged in `plan.md`
 * as touched by an in-flight Firebase-login migration on this branch. Per
 * this task's instructions, the committed state of both files in this
 * worktree (`a6c16fa`) is the porting baseline; nothing here duplicates
 * `AuthContext` (task 009) or `useCurrentUser` (task 010) — see
 * `useCustomerQuery.js`'s doc-comment for the full endpoint-by-endpoint
 * mapping.
 *
 * DataGrid vs Table: the task brief ("Requisitos Técnicos") asks to
 * "substituir tabela/paginação/filtros Materialize por MUI DataGrid". Reading
 * the legacy template closely: `customers.html`'s `ng-init="page=1"` (line 7)
 * initializes a `page` scope variable that is never read anywhere else in
 * the template or `customersController.js` (confirmed via
 * `grep -n "page" app/customer/customers.html app/customer/
 * customersController.js` — the only hit is that one dead `ng-init`) — i.e.
 * the legacy screen has no real pagination, sorting, or column filtering to
 * justify introducing `@mui/x-data-grid` (not a `client/package.json`
 * dependency today, unlike `@mui/material`/`@mui/icons-material`). Every
 * other listing module ported so far under near-identical constraints
 * (`MigrationListPage`, `HistoriesPage`, `ClippingsListPage`,
 * `ActionAuditPage`) already made and documented this same call: use plain
 * MUI `Table`, not `DataGrid`, when there is no real
 * pagination/sort/filter behavior to replicate and the row actions are
 * numerous per-row buttons (this screen has the most of any list screen
 * ported: 8 possible actions per row) that do not fit `DataGrid`'s
 * column-oriented API any better than a plain `Table` does. Introducing a
 * brand-new dependency here — for one module, against the grain of every
 * sibling list screen, to port a single dead `ng-init` — would be
 * inconsistent rather than faithful. Documented here as the explicit
 * decision for this task's `DataGrid` wording, per this task's own
 * instructions to document rather than silently deviate; see also this
 * task's "Notas de Execução" in `025-module-customer-list.md`.
 *
 * Screen behavior ported 1:1 from `customersController.js`:
 * - Single "email" filter + "Buscar" button (`getContracts(email)`): resets
 *   `contracts`/bounce/complaint state and loads the full list for that
 *   email (never appends — matches `processRespCallback`'s `contracts =
 *   []` reset in `getContracts` followed by a full push of the response).
 * - `loadFirst()` fires three requests concurrently on every search:
 *   `findBounceToEmail`, `findComplaintToEmail`, and
 *   `CustomerService.getContracts` — reproduced as three independent
 *   mutations kicked off together, not sequenced.
 * - "Mostrando: N" label, shown only once there is at least one row
 *   (`ng-show="contracts.length"`).
 * - Loading indicator (`#fountainG`/Materialize indeterminate `.progress`,
 *   toggled by `$scope.loading`) -> MUI `LinearProgress`; `loading` in the
 *   legacy controller is only ever set by `getContracts`/`loadFirst`'s own
 *   promise (not by bounce/complaint checks, and not by any row action) —
 *   reproduced here as `searchMutation.isPending` only.
 * - Per-row actions (all `ng-click` handlers in the `<td>` "Ações" column):
 *   backup (with its own email-confirmation modal), "habilitar limpeza de
 *   conta" (navigates to `workspace.customerProfile`, task 026 scope — this
 *   page only owns the navigation trigger), unblock email (only shown while
 *   `hasBounce === true`, exactly like the legacy `ng-if`), update pending
 *   bank slips, reset Elasticsearch indexes (gated by `allowedToResetElastic`
 *   — see `useCurrentUser()` — with its own index-selection modal), remove
 *   lawsuit-link search errors, and (gated by `allowedToManageSubscriptions`)
 *   first-login-token generation (only while
 *   `contract.userStatus === 'AWAITING_REGISTRATION'`), return-to-trial and
 *   return-to-light (only while `contract.regStatus` is `'ACTIVE'` or
 *   `'EXPIRED'`) — every visibility condition ported verbatim from the
 *   matching `ng-if` in `customers.html`.
 * - Oroboro sync icon button next to "Recursos" column
 *   (`syncUserPlanWithOroboro(contract.tenantId)`) — always visible, no
 *   `ng-if` gate in the legacy template.
 * - `returnToTrial`/`returnToLight`/`getFirstLoginToken` each go through a
 *   confirmation step in the legacy controller (`openConfirm(title, message,
 *   action)`, a single shared confirmation-modal helper) — ported as one
 *   shared MUI `Dialog` driven by a `confirmationModal` state object,
 *   mirroring the legacy `$scope.confirmationModal` shape 1:1 rather than
 *   three separate dialogs.
 * - Errors from `returnToTrial`/`returnToLight`/`getFirstLoginToken`
 *   (`handleError`) parse `error.data.errorMessage`/`error.data.message`
 *   (object or JSON-string) with a hardcoded fallback message per action —
 *   ported as `resolveErrorMessage(error, fallback)` below.
 * - `copyContentToClipboard`: legacy used a hidden `<textarea>` +
 *   `document.execCommand('copy')` (deprecated). Ported using the modern
 *   `navigator.clipboard.writeText` API instead — behaviorally equivalent
 *   (copies the token to the clipboard) but not byte-for-byte the same
 *   mechanism; flagged here since the task's instructions call for
 *   documenting rather than silently carrying over deprecated browser APIs
 *   when a direct modern equivalent exists and every target browsers this
 *   app (Chrome-based, internal tool) already support.
 *
 * NOT ported (dead code in the legacy controller/template, confirmed by
 * reading both in full):
 * - `canReturnToLight(contract)`/`canReturnToTrial(contract)`: computed on
 *   `$scope` but never referenced by any `ng-if`/`ng-show` in
 *   `customers.html` (confirmed via `grep -n "canReturnToLight\|
 *   canReturnToTrial" app/customer/customers.html` — no hits). The actual
 *   visibility gate for both actions in the template is only
 *   `contract.regStatus == 'ACTIVE' || contract.regStatus == 'EXPIRED'`
 *   (ported above) — these two functions are unused, so not wired to
 *   anything here either.
 * - `openUpdateSnapshotModal`/`cancelUpdateSnapshot`/`updateSnapshot`
 *   (`SnapshotService`): the entire snapshot-update modal is commented out
 *   in `customers.html` ("Snapshots have been replaced by Elasticsearch...")
 *   — not ported, matching the legacy screen's actual rendered state.
 * - `allowedToGenerateAPIKey`/`openCreateApplicationModal()`: referenced in
 *   `customers.html` (the "API Key" section, only shown when exactly one
 *   contract is loaded) but neither `allowedToGenerateAPIKey` nor
 *   `openCreateApplicationModal` are ever defined anywhere in
 *   `customersController.js` or `app/main.js` (confirmed via `grep -rn
 *   "allowedToGenerateAPIKey\|openCreateApplicationModal" app/` — only the
 *   two template references exist) — i.e. `allowedToGenerateAPIKey` is
 *   always `undefined`/falsy in production and the button has never been
 *   reachable. Not ported; flagged in this task's "Notas de Execução"
 *   instead of inventing an API-key creation flow.
 * - `editCustomer(customer)` (`$state.go('workspace.customerEdit', ...)`):
 *   `workspace.customerEdit` is not a registered UI-Router state anywhere in
 *   `app/main.js` (confirmed via `grep -n "customerEdit" app/main.js` — no
 *   hits) and `customers.html` never calls `editCustomer` from any element
 *   either (confirmed via `grep -n "editCustomer" app/customer/
 *   customers.html` — no hits) — dead controller code with no live UI
 *   trigger and no valid navigation target. Not ported.
 *
 * Not ported (presentational only, per `plan.md`'s UI-kit decision): the
 * `#fountainG`/`.wrap-responsive`/`inline-flex` Materialize layout chrome
 * and the `$(document).ready(...)` jQuery dropdown initialization.
 */

const ELASTIC_INDEXES = [
  { index: 'CONTACT', feature: 'Buscas de contatos' },
  { index: 'LAWSUIT', feature: 'Buscas e listagem de processos' },
  { index: 'CALENDAR_ACTIVITY', feature: 'Indicadores e contadores de atividades da agenda' },
]

/**
 * Port of `POLICY_LIMITS` (`customersController.js`). Only `MINIMUM_PLAN_EDITION`
 * is ported here — `MAX_DAYS_FOR_TRIAL_REVERSAL`/`MAX_DAYS_ACCOUNT_AGE_FOR_LIGHT`
 * back `$scope.canReturnToTrial`/`$scope.canReturnToLight`, which are
 * confirmed dead code in the legacy template (`grep -n "canReturnToTrial\|
 * canReturnToLight" app/customer/customers.html` finds no callers — the
 * "Voltar para Trial"/"Voltar para Light" buttons are gated inline by
 * `contract.regStatus == 'ACTIVE' || contract.regStatus == 'EXPIRED'`
 * instead, already ported as-is below), so porting the rest of the object
 * would just be unused code.
 */
const POLICY_LIMITS = {
  MINIMUM_PLAN_EDITION: 'LIGHT_1GB',
}

const BACKUP_TYPE_LABELS = { TOTAL: 'Completo', DIFFERENTIAL: 'Diferencial' }

const ROW_TABS = { CONTRACTS: 'contracts', BACKUP: 'backup', MORE: 'more' }

const EMPTY_BACKUP_HISTORY_STATE = Object.freeze({
  data: [],
  loading: false,
  error: '',
  loaded: false,
})

/**
 * Port of `$scope.canReturnToExpired(contract)` (commit b893e57, "feat(tenant
 * status): permite passar cliente para expirado") — unlike
 * `canReturnToTrial`/`canReturnToLight` above, this one IS referenced by the
 * legacy template (`ng-if="canReturnToExpired(contract)"`), gating the
 * "Passar Cliente para Expirado" action.
 */
function canReturnToExpired(contract) {
  return Boolean(
    contract &&
    (contract.regStatus === 'TRIAL' ||
      (contract.regStatus === 'ACTIVE' &&
        contract.planEdition === POLICY_LIMITS.MINIMUM_PLAN_EDITION)),
  )
}

function formatDateTime(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString('pt-BR')
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '-'
  const number = Number(value)
  if (Number.isNaN(number)) return String(value)
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatPendingValue(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value))
    return value.length ? value.map(formatPendingValue).filter(Boolean).join(', ') : null
  if (typeof value === 'object') return formatObjectSummary(value)
  return String(value)
}

function labelizePendingKey(key) {
  const labels = {
    id: 'ID',
    name: 'Nome',
    code: 'Código',
    type: 'Tipo',
    plan: 'Plano',
    edition: 'Edição',
    recurrence: 'Recorrência',
    quantity: 'Quantidade',
    value: 'Valor',
    discountValue: 'Desconto',
    percentage: 'Percentual',
  }
  return (
    labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())
  )
}

function formatTimestampDate(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (Number.isNaN(number)) return String(value)
  const parsed = new Date(number)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString('pt-BR')
}

function formatObjectSummary(object) {
  if (!object || typeof object !== 'object') return null
  const entries = Object.entries(object)
    .map(([key, value]) => {
      const formatted = key === 'dueDate' ? formatTimestampDate(value) : formatPendingValue(value)
      return formatted ? `${labelizePendingKey(key)}: ${formatted}` : null
    })
    .filter(Boolean)
  return entries.join(' | ')
}

const PENDING_CONTRACT_STATUSES = ['accepted', 'discarded', 'error', 'pending']

const PENDING_STATUS_LABELS = {
  accepted: 'Aceito',
  discarded: 'Descartado',
  error: 'Erro',
  pending: 'Pendente',
}

const PENDING_STATUS_ICON_COMPONENTS = {
  accepted: CheckCircleIcon,
  discarded: CancelIcon,
  error: ErrorIcon,
  pending: HourglassEmptyIcon,
}

const PENDING_STATUS_ICON_COLORS = {
  accepted: '#4caf50',
  discarded: '#9e9e9e',
  error: '#f44336',
  pending: '#ed6c02',
}

function pendingStatusLabel(status) {
  return PENDING_STATUS_LABELS[String(status || '').toLowerCase()] || status
}

function pendingStatusIcon(status) {
  const StatusIcon = PENDING_STATUS_ICON_COMPONENTS[String(status || '').toLowerCase()]
  if (!StatusIcon) return undefined
  const iconColor = PENDING_STATUS_ICON_COLORS[String(status).toLowerCase()]
  return <StatusIcon fontSize="small" sx={{ color: iconColor }} />
}

const PENDING_OPERATION_TYPE_LABELS = {
  ativacao_light: 'Ativação Light',
  alteracao_forma_pagamento: 'Alteração de Forma de Pagamento',
  compra_turbo: 'Compra de Turbo',
  remocao_turbo: 'Remoção de Turbo',
  nova_contratacao_com_turbo: 'Nova Contratação com Turbo',
  nova_contratacao: 'Nova Contratação',
  alteracao_plano_ou_recorrencia: 'Alteração de Plano ou Recorrência',
}

const PENDING_OPERATION_TYPE_COLORS = {
  ativacao_light: 'default',
  alteracao_forma_pagamento: 'secondary',
  compra_turbo: 'success',
  remocao_turbo: 'warning',
  nova_contratacao_com_turbo: 'primary',
  nova_contratacao: 'primary',
  alteracao_plano_ou_recorrencia: 'secondary',
}

function pendingOperationTypeLabel(operationType) {
  const normalized = String(operationType || '').toLowerCase()
  return (
    PENDING_OPERATION_TYPE_LABELS[normalized] ||
    normalized.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())
  )
}

function pendingOperationTypeColor(operationType) {
  return PENDING_OPERATION_TYPE_COLORS[String(operationType || '').toLowerCase()] || 'default'
}

function pendingPaymentMethodIcon(paymentMethod) {
  const normalized = String(paymentMethod || '').toLowerCase()
  if (normalized === 'boleto') return <ReceiptLongIcon fontSize="small" />
  if (normalized === 'cartao' || normalized === 'cartão') return <CreditCardIcon fontSize="small" />
  return undefined
}

function pendingRecurrenceColor(recurrence) {
  const normalized = String(recurrence || '').toLowerCase()
  if (normalized === 'anual') return 'secondary'
  if (normalized === 'mensal') return 'primary'
  return 'default'
}

function pendingContractKey(contract, index) {
  return (
    contract.id || `${contract.date || 'sem-data'}-${contract.operationType || 'operacao'}-${index}`
  )
}

function renderPendingContractsBlock(pendingContracts, statusFilter) {
  const filteredPendingContracts = pendingContracts.filter(
    (pendingContract) =>
      !pendingContract.status ||
      statusFilter === 'all' ||
      String(pendingContract.status).toLowerCase() === statusFilter,
  )
  if (!filteredPendingContracts.length) return null
  const visiblePendingContracts = filteredPendingContracts.slice(0, 10)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Ultimas 10 contratações:
      </Typography>

      <Stack spacing={1.5}>
        {visiblePendingContracts.map((pendingContract, index) => {
          const planSummary = formatObjectSummary(pendingContract.plan)

          return (
            <Box key={pendingContractKey(pendingContract, index)}>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                {pendingContract.operationType && (
                  <Chip
                    size="small"
                    color={pendingOperationTypeColor(pendingContract.operationType)}
                    label={pendingOperationTypeLabel(pendingContract.operationType)}
                  />
                )}
                {pendingContract.status && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={pendingStatusIcon(pendingContract.status)}
                    label={pendingStatusLabel(pendingContract.status)}
                  />
                )}
                {pendingContract.paymentMethod && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={pendingPaymentMethodIcon(pendingContract.paymentMethod)}
                    label={pendingContract.paymentMethod}
                  />
                )}
                {pendingContract.recurrence && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={pendingRecurrenceColor(pendingContract.recurrence)}
                    label={pendingContract.recurrence}
                  />
                )}
              </Stack>

              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Criado em: {formatDateTime(pendingContract.date)}
              </Typography>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 0.5, md: 3 }}
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  ID: {pendingContract.id || '-'}
                </Typography>
                <Typography variant="caption">
                  Aplicado em: {formatDateTime(pendingContract.appliedAt)}
                </Typography>
                <Typography variant="caption">Usuário: {pendingContract.userId || '-'}</Typography>
                <Typography variant="caption">
                  Parcelas: {pendingContract.installments || '-'}
                </Typography>
                <Typography variant="caption">
                  Parcela: {formatMoney(pendingContract.installmentValue)}
                </Typography>
                <Typography variant="caption">
                  Total: {formatMoney(pendingContract.totalValue)}
                </Typography>
                <Typography variant="caption">
                  Compra recurso adicional:{' '}
                  {formatPendingValue(pendingContract.additionalResourcePurchase) || '-'}
                </Typography>
              </Stack>

              {planSummary && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                    Plano:
                  </Typography>{' '}
                  {planSummary}
                </Typography>
              )}

              {(pendingContract.additionalResources || []).map((resource, resourceIndex) => (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5 }}
                  // Additional resources have no stable id in the API response, and this
                  // read-only list is never reordered/added/removed in place, so the array
                  // index is used as the key.
                  key={resourceIndex}
                >
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                    Recurso adicional:
                  </Typography>{' '}
                  {formatObjectSummary(resource) || '-'}
                </Typography>
              ))}

              {(pendingContract.discountCoupons || []).map((coupon, couponIndex) => (
                // Discount coupons have no stable id in the API response, and this
                // read-only list is never reordered/added/removed in place, so the array
                // index is used as the key.
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} key={couponIndex}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                    Cupom:
                  </Typography>{' '}
                  {formatObjectSummary(coupon) || '-'}
                </Typography>
              ))}

              {pendingContract.error && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {pendingContract.error}
                </Alert>
              )}

              {index < visiblePendingContracts.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

/** Port of `handleError(error, defaultMsg)`. */
function resolveErrorMessage(error, defaultMessage) {
  const data = error?.response?.data ?? error?.data
  if (data) {
    if (typeof data === 'object') {
      return data.errorMessage || data.message || defaultMessage
    }
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data)
        return parsed.errorMessage || parsed.message || defaultMessage
      } catch {
        return data
      }
    }
  }
  return error?.message || defaultMessage
}

function CustomersListPage() {
  const navigate = useNavigate()
  const { allowedToResetElastic, allowedToManageSubscriptions, allowedToLoadBackup, user } =
    useCurrentUser()
  const allowedToGenerateJwt = user?.allowedToGenerateJwt ?? false

  const searchMutation = useCustomerSearchMutation()
  const bounceCheckMutation = useBounceCheckMutation()
  const complaintCheckMutation = useComplaintCheckMutation()
  const unblockEmailMutation = useUnblockEmailMutation()
  const backupMutation = useBackupMutation()
  const backupHistoryMutation = useBackupHistoryMutation()
  const forceFullBackupMutation = useForceFullBackupMutation()
  const resetTwoFactorMutation = useResetTwoFactorMutation()
  const bankSlipsMutation = useUpdatePendingBankSlipsMutation()
  const oroboroMutation = useSyncOroboroMutation()
  const removeSearchErrorsMutation = useRemoveSearchResultsWithErrorsMutation()
  const resetElasticMutation = useResetElasticIndexMutation()
  const pendingContractsMutation = usePendingContractsMutation()
  const returnToTrialMutation = useReturnToTrialMutation()
  const returnToExpiredMutation = useReturnToExpiredMutation()
  const returnToLightMutation = useReturnToLightMutation()
  const firstLoginTokenMutation = useFirstLoginTokenMutation()

  const [email, setEmail] = useState('')
  const [contracts, setContracts] = useState([])
  const [pendingContractsByTenant, setPendingContractsByTenant] = useState({})
  const [pendingStatusFilter, setPendingStatusFilter] = useState('all')
  const pendingContractsRequestId = useRef(0)

  function togglePendingStatusFilter(status) {
    setPendingStatusFilter((current) => (current === status ? 'all' : status))
  }
  const [hasBounce, setHasBounce] = useState(false)
  const [bounceReason, setBounceReason] = useState('')
  const [hasComplaint, setHasComplaint] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  const [backupTarget, setBackupTarget] = useState(null)
  const [backupEmail, setBackupEmail] = useState('')

  const [activeTabByRow, setActiveTabByRow] = useState({})
  const [backupHistoryByUser, setBackupHistoryByUser] = useState({})
  const backupHistoryRequestId = useRef({})

  const [elasticTarget, setElasticTarget] = useState(null)
  const [selectedElasticIndexes, setSelectedElasticIndexes] = useState(['LAWSUIT'])

  const [confirmationModal, setConfirmationModal] = useState({
    opened: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  /** Port of `openConfirm(title, message, action)`. */
  function openConfirm(title, message, action) {
    setConfirmationModal({ opened: true, title, message, onConfirm: action })
  }

  function closeConfirmationModal() {
    setConfirmationModal({ opened: false, title: '', message: '', onConfirm: null })
  }

  function handleConfirmAction() {
    const action = confirmationModal.onConfirm
    closeConfirmationModal()
    if (typeof action === 'function') action()
  }

  /** Port of `getContracts(email)` + `loadFirst()`: fires all three requests together. */
  function handleSearch() {
    const trimmedEmail = email.trim()
    const requestId = pendingContractsRequestId.current + 1
    pendingContractsRequestId.current = requestId
    setContracts([])
    setPendingContractsByTenant({})
    setHasBounce(false)
    setHasComplaint(false)
    setBounceReason('')
    setActiveTabByRow({})
    setBackupHistoryByUser({})
    backupHistoryRequestId.current = {}

    bounceCheckMutation.mutate(trimmedEmail, {
      onSuccess: (data) => {
        setBounceReason(data.bounceReason)
        setHasBounce(data.hasBounce)
      },
    })
    complaintCheckMutation.mutate(trimmedEmail, {
      onSuccess: (data) => setHasComplaint(data),
    })
    searchMutation.mutate(trimmedEmail, {
      onSuccess: (data) => {
        setContracts(data)
        data.forEach((contract) => {
          if (!contract?.tenantId) return

          pendingContractsMutation
            .mutateAsync(contract.tenantId)
            .then((pendingContracts) => {
              if (
                pendingContractsRequestId.current !== requestId ||
                !Array.isArray(pendingContracts) ||
                pendingContracts.length === 0
              ) {
                return
              }
              setPendingContractsByTenant((current) => ({
                ...current,
                [contract.tenantId]: pendingContracts,
              }))
            })
            .catch(() => {})
        })
      },
      onError: () =>
        notify('Ocorreu um erro ao buscar o cliente! Não foi possível realizar a operação.'),
    })
  }

  /** Port of `editCleanAccountPermisison(customer)`. */
  function handleEditCleanAccountPermission(customer) {
    const { tenantId, namespace, isCleanAccountAllowed } = customer
    const profile = { tenantId, namespace, isCleanAccountAllowed }
    navigate(`/workspace/edit-customer-profile/${encodeURIComponent(JSON.stringify(profile))}`)
  }

  /** Port of `unblockEmail(email)`. */
  function handleUnblockEmail() {
    unblockEmailMutation.mutate(email, {
      onSuccess: (response) => {
        if (response.status === 200) setHasBounce(false)
      },
    })
  }

  function handleOpenBackupModal(customer) {
    setBackupTarget(customer)
    setBackupEmail('')
  }

  function handleCancelBackup() {
    setBackupTarget(null)
    setBackupEmail('')
  }

  /** Port of `startBackup()`. */
  function handleStartBackup() {
    const customer = backupTarget
    setBackupTarget(null)

    backupMutation.mutate(
      { userId: customer.userId, email: backupEmail },
      {
        onSuccess: () => {
          setBackupEmail('')
          notify('O backup está sendo gerado. Aguarde o recebimento do email.')
          refreshBackupHistoryIfOpened(customer)
        },
      },
    )
  }

  /** Port of `getBackupTypeLabel(type)`. */
  function getBackupTypeLabel(type) {
    return BACKUP_TYPE_LABELS[type] || type || '-'
  }

  function backupHistoryKey(contract) {
    return contract?.userId
  }

  function sortBackupHistoryByDateDesc(data) {
    return [...(data || [])].sort((a, b) => {
      const aTime = new Date(a?.createdAt).getTime()
      const bTime = new Date(b?.createdAt).getTime()
      return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
    })
  }

  function loadBackupHistory(contract, { force = false, silent = false } = {}) {
    const userId = contract?.userId
    if (!userId || !allowedToLoadBackup) return

    const key = backupHistoryKey(contract)
    const existing = backupHistoryByUser[key]
    if (!force && (existing?.loading || existing?.loaded)) return

    const requestId = (backupHistoryRequestId.current[key] || 0) + 1
    backupHistoryRequestId.current[key] = requestId

    setBackupHistoryByUser((current) => ({
      ...current,
      [key]: { data: existing?.data || [], loading: true, error: '', loaded: false },
    }))

    backupHistoryMutation.mutateAsync(userId).then(
      (data) => {
        if (backupHistoryRequestId.current[key] !== requestId) return
        setBackupHistoryByUser((current) => ({
          ...current,
          [key]: { data: sortBackupHistoryByDateDesc(data), loading: false, error: '', loaded: true },
        }))
      },
      () => {
        if (backupHistoryRequestId.current[key] !== requestId) return
        const message = 'Ocorreu um erro ao buscar o histórico de backups.'
        setBackupHistoryByUser((current) => ({
          ...current,
          [key]: { data: current[key]?.data || [], loading: false, error: message, loaded: false },
        }))
        if (!silent) notify(message)
      },
    )
  }

  function refreshBackupHistoryIfOpened(contract) {
    if (backupHistoryByUser[backupHistoryKey(contract)]) {
      loadBackupHistory(contract, { force: true, silent: true })
    }
  }

  function handleForceFullBackup(customer) {
    if (!customer?.userId) return notify('Usuário não informado para apagar histórico de backups.')

    openConfirm(
      'Forçar backup completo',
      `Isso vai apagar todo o histórico de backups de ${customer.namespace || customer.email} e o próximo backup solicitado será completo (em vez de parcial). Deseja continuar?`,
      () => {
        forceFullBackupMutation.mutateAsync(customer.userId).then(
          () => {
            notify('Histórico de backups apagado. O próximo backup solicitado será completo.')
            refreshBackupHistoryIfOpened(customer)
          },
          (error) => notify(resolveErrorMessage(error, 'Erro ao apagar histórico de backups.')),
        )
      },
    )
  }

  function handleResetTwoFactor(customer) {
    if (!customer?.userId) return notify('Usuário não informado para resetar o 2FA.')

    openConfirm(
      'Resetar 2FA',
      `Isso vai resetar a autenticação de dois fatores do usuário ${email || customer.userId} deseja continuar?`,
      () => {
        resetTwoFactorMutation.mutateAsync(customer.userId).then(
          () => notify('2FA resetado com sucesso.'),
          (error) => notify(resolveErrorMessage(error, 'Erro ao resetar o 2FA.')),
        )
      },
    )
  }

  function handleRowTabChange(rowKey, contract, newTab) {
    setActiveTabByRow((current) => ({ ...current, [rowKey]: newTab }))
    if (newTab === ROW_TABS.BACKUP) loadBackupHistory(contract)
  }

  function isSafeHttpUrl(value) {
    try {
      return ['http:', 'https:'].includes(new URL(value).protocol)
    } catch {
      return false
    }
  }

  /** Port of `updatePendingBankSlips(tenantId)`. */
  function handleUpdatePendingBankSlips(tenantId) {
    bankSlipsMutation.mutate(tenantId, {
      onSuccess: () => notify('A atualização dos boletos pendentes foi iniciada com sucesso.'),
    })
  }

  /** Port of `syncUserPlanWithOroboro(tenantId)` (working half only — see useCustomerQuery.js doc-comment). */
  function handleSyncOroboro(tenantId) {
    oroboroMutation.mutate(tenantId, {
      onSuccess: () =>
        notify('A sincronização do plano de uso com o Oroboro foi iniciada com sucesso.'),
    })
  }

  /** Port of `removeSearchResultsWithErrors(tenantId)`. */
  function handleRemoveSearchResultsWithErrors(tenantId) {
    removeSearchErrorsMutation.mutate(tenantId, {
      onSuccess: () =>
        notify('Os Resultado de buscas de processos com erro estão sendo removidos.'),
    })
  }

  function handleOpenResetElasticModal(customer) {
    setElasticTarget(customer)
  }

  function handleCloseResetElasticModal() {
    setElasticTarget(null)
  }

  /** Port of `toggleElasticIndex(elasticIndex)`. */
  function toggleElasticIndex(elasticIndex) {
    setSelectedElasticIndexes((current) =>
      current.includes(elasticIndex)
        ? current.filter((item) => item !== elasticIndex)
        : [...current, elasticIndex],
    )
  }

  /** Port of `resetElasticIndex(tenantId)`. */
  function handleConfirmResetElastic() {
    const tenantId = elasticTarget?.tenantId
    resetElasticMutation.mutate(
      { tenantId, elasticIndexes: selectedElasticIndexes },
      {
        onSuccess: () => {
          setElasticTarget(null)
          notify('Os índices do elastic search foram resetados.')
        },
        onError: () => {
          setElasticTarget(null)
          notify('Ocorreu um erro com a solicitação. Confira os logs.')
        },
      },
    )
  }

  /**
   * Port of `returnToTrial(contract)`. Confirm message uses
   * `contract.namespace` (not `.name`) and the success handler updates both
   * `regStatus` and `statusDescription` — per commit b893e57's refactor into
   * the shared `changeTenantStatus` helper, which sets both fields (the
   * pre-b893e57 version only set `statusDescription`).
   */
  function handleReturnToTrial(contract) {
    if (!contract?.tenantId) return notify('Tenant ID não encontrado no contrato')

    openConfirm(
      'Retornar para TRIAL',
      `Deseja realmente retornar o escritório ${contract.namespace} para TRIAL?`,
      () => {
        returnToTrialMutation.mutate(contract, {
          onSuccess: () => {
            notify('Status alterado para TRIAL com sucesso!')
            setContracts((current) =>
              current.map((item) =>
                item === contract
                  ? { ...item, regStatus: 'TRIAL', statusDescription: 'TRIAL' }
                  : item,
              ),
            )
          },
          onError: (error) => notify(resolveErrorMessage(error, 'Erro ao retornar para Trial')),
        })
      },
    )
  }

  /**
   * Port of `returnToExpired(contract)` (commit b893e57, "feat(tenant
   * status): permite passar cliente para expirado") — same
   * `changeTenantStatus` helper as `returnToTrial` above, with `EXPIRED`.
   */
  function handleReturnToExpired(contract) {
    if (!contract?.tenantId) return notify('Tenant ID não encontrado no contrato')

    openConfirm(
      'Expirar cliente',
      `Deseja realmente expirar o escritório ${contract.namespace}?`,
      () => {
        returnToExpiredMutation.mutate(contract, {
          onSuccess: () => {
            notify('Status alterado para EXPIRADO com sucesso!')
            setContracts((current) =>
              current.map((item) =>
                item === contract
                  ? { ...item, regStatus: 'EXPIRED', statusDescription: 'EXPIRED' }
                  : item,
              ),
            )
          },
          onError: (error) => notify(resolveErrorMessage(error, 'Erro ao expirar cliente')),
        })
      },
    )
  }

  /** Port of `returnToLight(contract)`. */
  function handleReturnToLight(contract) {
    if (!contract?.tenantId) return notify('Tenant ID não encontrado no contrato')

    openConfirm(
      'Mudar para plano LIGHT',
      `Confirmar alteração do cliente ${contract.name} para o plano LIGHT?`,
      () => {
        returnToLightMutation.mutate(contract, {
          onSuccess: () => {
            notify('Status alterado para LIGHT com sucesso!')
            setContracts((current) =>
              current.map((item) => (item === contract ? { ...item, planEdition: 'LIGHT' } : item)),
            )
          },
          onError: (error) => notify(resolveErrorMessage(error, 'Erro ao retornar para Light')),
        })
      },
    )
  }

  /** Port of `getFirstLoginToken(contract)` + `copyContentToClipboard(text)`. */
  function handleGetFirstLoginToken(contract) {
    if (!contract?.tenantId) return notify('Tenant ID não encontrado')

    firstLoginTokenMutation.mutate(contract, {
      onSuccess: (token) => {
        if (token) navigator.clipboard?.writeText(token)
        notify('Link gerado e copiado para a área de transferência!')
      },
      onError: (error) => notify(resolveErrorMessage(error, 'Erro ao gerar Link de Ativação')),
    })
  }

  const isLoading = searchMutation.isPending

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Clientes
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            id="email"
            label="Digite um email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Buscar
          </Button>
        </Stack>
      </Paper>

      {contracts.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Mostrando: {contracts.length}
        </Typography>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status do Escritório</TableCell>
              <TableCell>Informações</TableCell>
              <TableCell>Telefone / Endereço</TableCell>
              <TableCell>Cadastro</TableCell>
              <TableCell>Recursos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map((contract, index) => {
              const pendingContracts = pendingContractsByTenant[contract.tenantId]
              const reactKey = `${contract.tenantId || ''}-${contract.userId || ''}-${index}`
              const activeTab = activeTabByRow[reactKey] || ROW_TABS.CONTRACTS
              const backupHistoryState =
                backupHistoryByUser[backupHistoryKey(contract)] || EMPTY_BACKUP_HISTORY_STATE

              return (
                <Fragment key={reactKey}>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        TENANT : {contract.statusDescription}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        USUÁRIO : {contract.userStatus}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        PERFIL : {contract.profile}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        TIPO : {contract.userType}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ACESSO : {contract.accessType}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ADMINISTRADOR : {contract.systemAdministrator}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        FINANCEIRO : {contract.hasAccessToFinancial}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        FATURAMENTO : {contract.hasAccessToMoneyInfo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        USUÁRIO: {contract.userId}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        NAMESPACE: {contract.namespace}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        TENANT: {contract.tenantId}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ESCRITÓRIO: {contract.name}
                      </Typography>
                      {contract.cpf && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          CPF: {contract.cpf}
                        </Typography>
                      )}
                      {contract.cnpj && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          CNPJ: {contract.cnpj}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ROBOS SERIAL: {contract.serialNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ROBOS SENHA: {contract.caronteAccInfoPassword}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ROBOS KEY: {contract.caronteAccInfoKey}
                      </Typography>
                      {contract.clippingsProvider && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          PUBLICAÇÕES: {contract.clippingsProvider}
                        </Typography>
                      )}
                      {contract.groupCode && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          COD. ESCRITÓRIO: {contract.groupCode}
                        </Typography>
                      )}
                      {(contract.emails || []).map((contractEmail) => (
                        <Typography variant="caption" sx={{ display: 'block' }} key={contractEmail}>
                          RESP. FIN: {contractEmail}
                        </Typography>
                      ))}
                      {hasBounce && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block' }}
                          title={bounceReason}
                        >
                          <b>FOI ENCONTRADO UM BLOQUEIO PARA O EMAIL DESTE USUÁRIO!</b>
                        </Typography>
                      )}
                      {hasComplaint && (
                        <Typography variant="caption" sx={{ display: 'block' }} color="error">
                          <b>ESTE DESTINATÁRIO MARCOU O ASTREA COMO SPAM!</b>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(contract.numbers || []).map((number) => (
                        <Typography variant="caption" sx={{ display: 'block' }} key={number}>
                          {number}
                        </Typography>
                      ))}
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {contract.address}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {contract.zipCityState}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{contract.creationDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Plano no Astrea: {contract.planEdition}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Usuários: {contract.totalUsers}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Créditos: {contract.totalCredits}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Espaço: {contract.totalSpace}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Publicações: {contract.totalClippings}
                      </Typography>
                      <Tooltip title="Sincronizar com o Oroboro">
                        <IconButton
                          size="small"
                          aria-label="Sincronizar com o Oroboro"
                          onClick={() => handleSyncOroboro(contract.tenantId)}
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ pt: 0 }}>
                      <Tabs
                        value={activeTab}
                        onChange={(event, newTab) => handleRowTabChange(reactKey, contract, newTab)}
                        sx={{ minHeight: 40, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Tab value={ROW_TABS.CONTRACTS} label="Contratações" sx={{ minHeight: 40 }} />
                        <Tab value={ROW_TABS.BACKUP} label="Backup" sx={{ minHeight: 40 }} />
                        <Tab value={ROW_TABS.MORE} label="Mais ações" sx={{ minHeight: 40 }} />
                      </Tabs>

                      {activeTab === ROW_TABS.CONTRACTS &&
                        (pendingContracts?.length > 0 ? (
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}
                            >
                              <Typography variant="body2">Filtrar contratações:</Typography>
                              {PENDING_CONTRACT_STATUSES.map((status) => {
                                const isStatusSelected = pendingStatusFilter === status
                                return (
                                  <Chip
                                    key={status}
                                    size="small"
                                    label={pendingStatusLabel(status)}
                                    icon={pendingStatusIcon(status)}
                                    color={isStatusSelected ? 'primary' : 'default'}
                                    variant={isStatusSelected ? 'filled' : 'outlined'}
                                    onClick={() => togglePendingStatusFilter(status)}
                                  />
                                )
                              })}
                              {pendingStatusFilter !== 'all' && (
                                <Button size="small" onClick={() => setPendingStatusFilter('all')}>
                                  Selecionar todos
                                </Button>
                              )}
                            </Stack>
                            {renderPendingContractsBlock(pendingContracts, pendingStatusFilter)}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhuma contratação encontrada para este cliente.
                          </Typography>
                        ))}

                      {activeTab === ROW_TABS.BACKUP && (
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<BackupIcon fontSize="small" />}
                              onClick={() => handleOpenBackupModal(contract)}
                            >
                              Novo backup
                            </Button>
                            {allowedToGenerateJwt && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<DeleteSweepIcon fontSize="small" />}
                                onClick={() => handleForceFullBackup(contract)}
                              >
                                Forçar completo
                              </Button>
                            )}
                          </Stack>

                          {!contract.userId ? (
                            <Alert severity="error">
                              Usuário não informado para buscar histórico de backups.
                            </Alert>
                          ) : allowedToLoadBackup ? (
                            <>
                              {backupHistoryState.loading && <LinearProgress sx={{ mb: 2 }} />}
                              {backupHistoryState.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  {backupHistoryState.error}
                                </Alert>
                              )}
                              {!backupHistoryState.loading &&
                                !backupHistoryState.error &&
                                backupHistoryState.loaded &&
                                backupHistoryState.data.length === 0 && (
                                  <Typography variant="body2" color="text.secondary">
                                    Nenhum backup foi realizado ainda para este cliente.
                                  </Typography>
                                )}
                              {backupHistoryState.data.length > 0 && (
                                <>
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    Último backup: <b>{formatDateTime(backupHistoryState.data[0].createdAt)}</b>{' '}
                                    · Tipo: <b>{getBackupTypeLabel(backupHistoryState.data[0].type)}</b> · Status:{' '}
                                    <b>{backupHistoryState.data[0].status || '-'}</b>
                                  </Typography>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Data</TableCell>
                                          <TableCell>Email</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Tipo</TableCell>
                                          <TableCell>Link</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {backupHistoryState.data.map((backup, backupIndex) => (
                                          // Backups have no stable id in the API response, so the array index is used as the key.
                                          <TableRow key={backupIndex}>
                                            <TableCell>
                                              <Typography variant="caption">
                                                {formatDateTime(backup.createdAt)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption">{backup.email || '-'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption">{backup.status || '-'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption">
                                                {getBackupTypeLabel(backup.type)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              {isSafeHttpUrl(backup.downloadUrl) ? (
                                                <Tooltip title="Abrir backup">
                                                  <IconButton
                                                    size="small"
                                                    aria-label="Abrir backup"
                                                    component="a"
                                                    href={backup.downloadUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    <CloudDownloadIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              ) : (
                                                <Typography variant="caption">Indisponível</Typography>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Você não tem permissão para ver o histórico de backups.
                            </Typography>
                          )}
                        </Box>
                      )}

                      {activeTab === ROW_TABS.MORE && (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReceiptLongIcon fontSize="small" />}
                            onClick={() => handleUpdatePendingBankSlips(contract.tenantId)}
                          >
                            Atualizar boletos pendentes
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CleaningServicesIcon fontSize="small" />}
                            onClick={() => handleEditCleanAccountPermission(contract)}
                          >
                            Habilitar limpeza de conta
                          </Button>
                          {hasBounce && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<MarkEmailReadIcon fontSize="small" />}
                              onClick={handleUnblockEmail}
                            >
                              Desbloquear email
                            </Button>
                          )}
                          {allowedToResetElastic && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ManageSearchIcon fontSize="small" />}
                              onClick={() => handleOpenResetElasticModal(contract)}
                            >
                              Normalizar sugestões de buscas no astrea
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlaylistRemoveIcon fontSize="small" />}
                            onClick={() => handleRemoveSearchResultsWithErrors(contract.tenantId)}
                          >
                            Limpar buscas com erro (aba Vínculos)
                          </Button>
                          {allowedToGenerateJwt && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<KeyIcon fontSize="small" />}
                              onClick={() => handleResetTwoFactor(contract)}
                            >
                              Resetar 2FA
                            </Button>
                          )}
                          {allowedToManageSubscriptions &&
                            contract.userStatus === 'AWAITING_REGISTRATION' && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VpnKeyIcon fontSize="small" />}
                                onClick={() => handleGetFirstLoginToken(contract)}
                              >
                                Gerar link de Ativação Primeiro Acesso
                              </Button>
                            )}
                          {allowedToManageSubscriptions &&
                            (contract.regStatus === 'ACTIVE' || contract.regStatus === 'EXPIRED') && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RestartAltIcon fontSize="small" />}
                                onClick={() => handleReturnToTrial(contract)}
                              >
                                Voltar Cliente para Trial
                              </Button>
                            )}
                          {allowedToManageSubscriptions && canReturnToExpired(contract) && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EventBusyIcon fontSize="small" />}
                              onClick={() => handleReturnToExpired(contract)}
                            >
                              Passar Cliente para Expirado
                            </Button>
                          )}
                          {allowedToManageSubscriptions &&
                            (contract.regStatus === 'ACTIVE' || contract.regStatus === 'EXPIRED') && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LightModeIcon fontSize="small" />}
                                onClick={() => handleReturnToLight(contract)}
                              >
                                Voltar Cliente para Light
                              </Button>
                            )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Port of the "backup-modal" confirmation dialog. */}
      <Dialog open={backupTarget !== null} onClose={handleCancelBackup}>
        <DialogTitle>Realizar Backup da conta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Para confirmar o backup da conta digite o email que deseja receber o link para download
            do arquivo.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Somente os endereços de e-mail do cliente ou com o final <code>@aurum.com.br</code>{' '}
            funcionarão.
          </DialogContentText>
          <TextField
            id="backupEmail"
            label="Digite um email"
            value={backupEmail}
            onChange={(event) => setBackupEmail(event.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBackup}>Cancelar</Button>
          <Button variant="contained" onClick={handleStartBackup} disabled={!backupEmail}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Port of the "resetElasticIndex-modal" dialog. */}
      <Dialog open={elasticTarget !== null} onClose={handleCloseResetElasticModal}>
        <DialogTitle>Sincronizar Elasticsearch</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Selecione quais funcionalidades você deseja sincronizar:
          </DialogContentText>
          <Stack>
            {ELASTIC_INDEXES.map(({ index, feature }) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedElasticIndexes.includes(index)}
                    onChange={() => toggleElasticIndex(index)}
                  />
                }
                label={feature}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetElasticModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmResetElastic}
            disabled={selectedElasticIndexes.length === 0 || resetElasticMutation.isPending}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Port of the shared "confirmation-modal" (returnToTrial/returnToLight/getFirstLoginToken). */}
      <Dialog open={confirmationModal.opened} onClose={closeConfirmationModal}>
        <DialogTitle>{confirmationModal.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmationModal.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmationModal}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmAction}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CustomersListPage
